import { FIELDS } from './fields.js';

/** One question on the ticket, drawn from FIELDS. Picklist values come from Salesforce. */
export default function AnswerField({ field, value, onChange, picklists, invalid, disabled, idSuffix = '', label, help }) {
  const base = FIELDS[field];
  if (!base) return null;
  // The model a model change moves to has its own picker in the ticket (series and equity), not a plain field.
  if (base.kind === 'model') return null;
  // A screen can word a question for its own situation (an optional instruction, say) without a second field.
  const def = { ...base, ...(label ? { label } : {}), ...(help !== undefined ? { help } : {}) };
  // The same question can appear once per account, so each copy needs its own id.
  const id = `tt-${field}${idSuffix ? `-${idSuffix}` : ''}`;
  const options = (picklists && picklists[def.picklist]) || [];
  const cls = 'tt-field' + (invalid ? ' is-invalid' : '') + (def.kind === 'textarea' ? ' is-wide' : '');

  if (def.kind === 'checkbox') {
    return (
      <label className={cls + ' is-check'} htmlFor={id}>
        <input id={id} type="checkbox" checked={Boolean(value)} disabled={disabled} onChange={e => onChange(field, e.target.checked)} />
        <span>{def.label}</span>
      </label>
    );
  }
  // An opt-out: on unless the advisor says otherwise, stored as Enrolled or Opted out.
  if (def.kind === 'optout') {
    return (
      <label className={cls + ' is-check'} htmlFor={id}>
        <input id={id} type="checkbox" checked={value !== 'Opted out'} disabled={disabled} onChange={e => onChange(field, e.target.checked ? 'Enrolled' : 'Opted out')} />
        <span>{def.label}{def.help && <span className="tt-help tt-inline-help"> {def.help}</span>}</span>
      </label>
    );
  }

  let input;
  if (def.kind === 'textarea') {
    input = <textarea id={id} rows={def.rows || 3} value={value || ''} disabled={disabled} onChange={e => onChange(field, e.target.value)} />;
  } else if (def.kind === 'picklist') {
    input = (
      <select id={id} value={value || ''} disabled={disabled} onChange={e => onChange(field, e.target.value)}>
        <option value="">Choose…</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else if (def.kind === 'multipicklist') {
    const chosen = new Set(String(value || '').split(';').filter(Boolean));
    input = (
      <div className="tt-chips" role="group" aria-labelledby={`${id}-label`}>
        {options.map(o => (
          <label key={o.value} className={'tt-chip' + (chosen.has(o.value) ? ' is-on' : '')}>
            <input
              type="checkbox"
              checked={chosen.has(o.value)}
              disabled={disabled}
              onChange={() => {
                const next = new Set(chosen);
                if (next.has(o.value)) next.delete(o.value); else next.add(o.value);
                onChange(field, [...next].join(';'));
              }}
            />
            {o.label}
          </label>
        ))}
      </div>
    );
  } else if (def.kind === 'date') {
    input = <input id={id} type="date" value={value || ''} disabled={disabled} onChange={e => onChange(field, e.target.value)} />;
  } else {
    const numeric = def.kind === 'currency' || def.kind === 'percent';
    input = (
      <div className={'tt-input' + (def.kind === 'currency' ? ' has-prefix' : '') + (def.kind === 'percent' ? ' has-suffix' : '')}>
        {def.kind === 'currency' && <span className="tt-affix">$</span>}
        <input
          id={id}
          type="text"
          inputMode={numeric ? 'decimal' : undefined}
          value={value ?? ''}
          disabled={disabled}
          onChange={e => onChange(field, numeric ? e.target.value.replace(/[^0-9.,-]/g, '') : e.target.value)}
        />
        {def.kind === 'percent' && <span className="tt-affix">%</span>}
      </div>
    );
  }

  return (
    <div className={cls}>
      <label id={`${id}-label`} htmlFor={id}>{def.label}</label>
      {input}
      {def.help && <p className="tt-help">{def.help}</p>}
    </div>
  );
}
